import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const GRNReturn = () => {
  const [step, setStep] = useState('list'); // list, select_items
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [returnableItems, setReturnableItems] = useState([]);
  const [returnItems, setReturnItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [returns, setReturns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchSuppliers(); fetchGRNs(); }, []);
  useEffect(() => { fetchGRNs(); }, [selectedSupplier]);
  useEffect(() => { if (showHistory) fetchReturns(); }, [showHistory]);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } });
      setSuppliers(resp.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchGRNs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = { page: 1, limit: 50, from_date: '2020-01-01', to_date: '2099-12-31' };
      if (selectedSupplier) params.supplier_id = selectedSupplier;
      const resp = await axios.get(`${API}/grn`, { headers: { Authorization: `Bearer ${token}` }, params });
      setGrns(resp.data.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchReturns = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/grn-returns`, { headers: { Authorization: `Bearer ${token}` } });
      setReturns(resp.data.records || []);
    } catch (e) { console.error(e); }
  };

  const selectGRN = async (grn) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/grn/${grn.id}/returnable-items`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedGRN(resp.data.grn);
      setReturnableItems(resp.data.returnable_items || []);
      setReturnItems(resp.data.returnable_items.map(item => ({
        ...item, return_qty: 0, return_reason: ''
      })));
      setStep('select_items');
    } catch (e) { alert(e.response?.data?.detail || 'Failed to load GRN'); } finally { setLoading(false); }
  };

  const updateItem = (idx, field, value) => {
    const updated = [...returnItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setReturnItems(updated);
  };

  const getItemsToReturn = () => returnItems.filter(i => i.return_qty > 0 && i.return_reason);
  const totalReturn = getItemsToReturn().reduce((s, i) => s + (i.return_qty * i.purchase_price), 0);

  const processReturn = async () => {
    const items = getItemsToReturn();
    if (!items.length) { alert('Select items with reason'); return; }
    setProcessing(true);
    try {
      const token = localStorage.getItem('pos_token');
      const supplier = suppliers.find(s => s.id === selectedGRN.supplier_id);
      await axios.post(`${API}/grn-return`, {
        grn_id: selectedGRN.id,
        grn_number: selectedGRN.grn_number,
        supplier_id: selectedGRN.supplier_id,
        items: items.map(i => ({
          product_id: i.product_id, product_name: i.product_name,
          quantity: parseFloat(i.return_qty), purchase_price: i.purchase_price, return_reason: i.return_reason
        })),
        notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('GRN return processed! Stock deducted and supplier ledger adjusted.');
      resetForm();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setProcessing(false); }
  };

  const resetForm = () => { setStep('list'); setSelectedGRN(null); setReturnItems([]); setNotes(''); fetchGRNs(); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div data-testid="grn-return-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">GRN Return</h1>
          <p className="text-slate-500">Return purchased items to supplier</p>
        </div>
        <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchReturns(); }}
          className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${showHistory ? 'bg-violet-600 text-white' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          data-testid="toggle-grn-return-history">
          {showHistory ? 'New Return' : 'Return History'}
        </button>
      </div>

      {showHistory ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Return ID</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">GRN No</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Supplier</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Items</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {returns.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No GRN returns found</td></tr>
              ) : returns.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-mono text-xs">GRNRTN-{r.id}</td>
                  <td className="px-4 py-3 font-medium">{r.grn_number}</td>
                  <td className="px-4 py-3">{r.supplier_name || '-'}</td>
                  <td className="px-4 py-3">{(r.items || []).map((item, i) => <div key={i} className="text-xs">{item.product_name} x{item.quantity} <span className="text-slate-400">({item.return_reason})</span></div>)}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">Rs.{(r.total_return_value || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : step === 'list' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm min-w-[200px]">
              <option value="">All Suppliers</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : (
            <div className="space-y-2">
              {grns.map(grn => (
                <button key={grn.id} onClick={() => selectGRN(grn)}
                  className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:bg-violet-50 hover:border-violet-300 transition-all" data-testid={`grn-option-${grn.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-slate-800">{grn.grn_number}</p>
                      <p className="text-sm text-slate-500">{grn.supplier_name} | Inv: {grn.invoice_number || '-'} | {formatDate(grn.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-violet-600">Rs.{(grn.net_amount || grn.total_amount || 0).toFixed(2)}</p>
                      <p className="text-xs text-slate-500">{(grn.items || []).length} items</p>
                    </div>
                  </div>
                </button>
              ))}
              {grns.length === 0 && <p className="text-center py-8 text-slate-400">No GRN entries found</p>}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold">{selectedGRN.grn_number}</h2>
              <p className="text-sm text-slate-500">Inv: {selectedGRN.invoice_number || '-'} | {formatDate(selectedGRN.created_at)} | Rs.{(selectedGRN.net_amount || 0).toFixed(2)}</p>
            </div>
            <button onClick={resetForm} className="text-sm text-violet-600 hover:text-violet-800">Change GRN</button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <h3 className="font-semibold text-slate-800">Select Items to Return</h3>
            {returnItems.map((item, idx) => (
              <div key={item.product_id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`grn-return-item-${idx}`}>
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{item.product_name}</p>
                    <p className="text-xs text-slate-500">Received: {item.original_qty} | Returned: {item.returned_qty} | Can Return: {item.returnable_qty} | Price: Rs.{item.purchase_price.toFixed(2)}</p>
                  </div>
                  {item.return_qty > 0 && <span className="text-sm font-bold text-red-600">Rs.{(item.return_qty * item.purchase_price).toFixed(2)}</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Return Qty</label>
                    <input type="number" value={item.return_qty} min={0} max={item.returnable_qty} step="1"
                      onChange={e => updateItem(idx, 'return_qty', Math.min(parseFloat(e.target.value) || 0, item.returnable_qty))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid={`grn-return-qty-${idx}`} />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Reason *</label>
                    <select value={item.return_reason} onChange={e => updateItem(idx, 'return_reason', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid={`grn-return-reason-${idx}`}>
                      <option value="">Select</option>
                      <option value="Defective">Defective</option>
                      <option value="Wrong Item">Wrong Item</option>
                      <option value="Expired">Expired</option>
                      <option value="Quality Issue">Quality Issue</option>
                      <option value="Damaged in Transit">Damaged in Transit</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalReturn > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Return Summary</h3>
                <p className="text-xl font-bold text-red-600">Rs.{totalReturn.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                Stock will be deducted and supplier ledger will be adjusted (reduces payable).
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Return notes..." />
              </div>
              <button onClick={processReturn} disabled={processing}
                className="w-full bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 disabled:opacity-50" data-testid="process-grn-return-btn">
                {processing ? 'Processing...' : `Process GRN Return - Rs.${totalReturn.toFixed(2)}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GRNReturn;
